import { Flex, Stack } from '@chakra-ui/react';
import {
  Briefcase,
  HeartHandshake,
  InboxIcon,
  LucideLayoutDashboard,
  LucideUserCircle2,
  LucideUsers,
  ScrollText,
} from 'lucide-react';
import { t } from '@lingui/core/macro';
import { useNavButton } from '@duke-hq/ui/components/MainSidebar/useNavButton';
import { NavButton } from '@duke-hq/ui/components/MainSidebar/NavButton';
import { ToggleButton } from '@duke-hq/ui/components/ToggleButton/ToggleButton';
import { sizes, sizeVars } from '@duke-hq/ui/constants/layout';
import { useIsWiderThan940px } from '@duke-hq/ui/components/hooks/useIsWiderThan940px';
import { CSSProperties, useMemo } from 'react';
import { useSubSidebar } from '@duke-hq/ui/components/SubSidebar/useSubSidebar';
import { useAuthTenant } from './useAuthTenant';
import { useTenantContext } from './TenantContext';
import { useFlag } from '@duke-hq/flags/react';
import { trpcTenant } from './trpcTenant';

export interface ISideBarMenu {
  href?: string;
  link: string;
  label: string;
  icon: React.ElementType;
  depth?: number;
  children?: ISideBarMenu[];
  iconStyles?: CSSProperties;
  role: string[];
  hidden?: boolean;
  onClick?: () => void;
  badge?: number;
}

function useMenu(): ISideBarMenu[] {
  const tenantCtx = useTenantContext();
  const INCENTIVE_PROGRAM_RELEASE = useFlag('INCENTIVE_PROGRAM_RELEASE');
  const WORK_PLACEMENT_FEATURE = useFlag('WORK_PLACEMENT_FEATURE');
  const { closeSubSidebar } = useSubSidebar();

  const { data: placementsData } = trpcTenant.workPlacements.hasPlacements.useQuery(undefined, {
    enabled: WORK_PLACEMENT_FEATURE === true,
  });
  const showPlacements = WORK_PLACEMENT_FEATURE === true && placementsData?.hasPlacements === true;

  const { data } = trpcTenant.notifications.unreadTicketCount.useQuery(undefined, {
    // Consider data fresh for 1 hour — SSE handles updates, this is a fallback
    staleTime: 1000 * 60 * 60,
    // Keep in cache for 1 hour
    gcTime: 1000 * 60 * 60,
    // Refetch on window focus as a safety net if the SSE connection drops silently
    refetchOnWindowFocus: true,
    // Don't refetch on reconnect - SSE handles updates
    refetchOnReconnect: false,
    // Don't refetch on mount - we have fresh data from SSR
    refetchOnMount: false,
  });

  const unreadCount = data?.unreadCount ?? 0;

  return useMemo(() => {
    const options: ISideBarMenu[] = [
      {
        label: t`Dashboard`,
        icon: LucideLayoutDashboard,
        link: '/',
        role: ['*'],
        onClick: closeSubSidebar,
      },
      {
        label: t`Inbox`,
        icon: InboxIcon,
        link: '/inbox',
        role: ['*'],
        badge: unreadCount > 0 ? unreadCount : undefined,
      },
      {
        label: t`Participants`,
        icon: LucideUserCircle2,
        link: '/students',
        role: ['*'],
      },
      {
        label: t`Enrolments`,
        icon: ScrollText,
        link: '/enrolments',
        role: ['*'],
      },
      ...(showPlacements
        ? [
            {
              label: t`Placements`,
              icon: Briefcase,
              link: '/placements/students',
              role: ['*'],
            },
          ]
        : []),
      ...(tenantCtx?.permissions.includes('canManageTeamMembers') === true
        ? [
            {
              label: t`Team`,
              icon: LucideUsers,
              link: '/team/members',
              role: ['*'],
            },
          ]
        : []),
      ...(typeof INCENTIVE_PROGRAM_RELEASE === 'boolean' &&
      INCENTIVE_PROGRAM_RELEASE &&
      tenantCtx?.settings.incentiveProgramEnabled === true
        ? [
            {
              label: t`Group Rewards`,
              icon: HeartHandshake,
              link: '/group_rewards',
              role: ['*'],
            },
          ]
        : []),
    ];

    return options;
  }, [unreadCount, showPlacements, closeSubSidebar, INCENTIVE_PROGRAM_RELEASE, tenantCtx]);
}

export function SidebarConsultant() {
  const { expanded, toggleExpanded } = useNavButton();
  const [isLargerThan940] = useIsWiderThan940px();

  return (
    <ToggleButton
      isOpen={expanded}
      onClick={toggleExpanded}
      aria-label="Open menu"
      display={{ base: 'inline-flex' }}
      hidden={isLargerThan940 !== true}
    />
  );
}

export function SideBarNavButtonsConsultant() {
  const { expanded } = useNavButton();
  const [isLargerThan940] = useIsWiderThan940px();

  return (
    <Flex
      w={expanded && isLargerThan940 === true ? sizes.sidebarExpanded : sizes.sidebarCollapsed}
      minW={expanded && isLargerThan940 === true ? sizes.sidebarExpanded : sizes.sidebarCollapsed}
      justifySelf={'start'}
      alignSelf={'start'}
      minH={`calc(100vh - ${sizeVars.navbar})`}
      overflow="hidden"
      bg="bg.subtle"
      p="2"
      borderRightWidth="1px"
      flexDirection="column"
      justifyContent="space-between"
    >
      <Stack alignItems={expanded ? 'flex-start' : 'center'} spacing="3" width="full">
        <SideBarNavButtons />
      </Stack>
    </Flex>
  );
}

function SideBarNavButtons() {
  const [isLargerThan940] = useIsWiderThan940px();
  const { expanded } = useNavButton();
  const auth = useAuthTenant();

  const filterMenuOptions = (
    userRole: string | undefined,
    menuOptions: ISideBarMenu[],
  ): ISideBarMenu[] => {
    return menuOptions.filter(
      (option) =>
        typeof userRole === 'string' &&
        (option.role.includes('*') || option.role.includes(userRole)) &&
        option.hidden !== true,
    );
  };

  const filteredMenuOptions = filterMenuOptions(auth.user.role, useMenu());

  return (
    <>
      {filteredMenuOptions.map((item, i) => (
        <NavButton
          key={i}
          label={item.label}
          icon={item.icon}
          link={item.link}
          badge={item.badge}
          iconStyles={item?.iconStyles ? item.iconStyles : {}}
          onClick={item.onClick ? item.onClick : () => {}}
          expanded={isLargerThan940 === true ? expanded : false}
        />
      ))}
    </>
  );
}
